import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ContentProvider, useContent } from "@/context/ContentContext";
import { CartProvider } from "@/context/CartContext";
import { AudioProvider } from "@/context/AudioContext";
import Nav from "@/components/Nav";
import PlayerBar from "@/components/PlayerBar";
import CartDrawer from "@/components/CartDrawer";
import Footer from "@/components/Footer";
import { LogoMark } from "@/components/Logo";
import Home from "@/pages/Home";
import Music from "@/pages/Music";
import Videos from "@/pages/Videos";
import Shop from "@/pages/Shop";
import Passport from "@/pages/Passport";
import Bio from "@/pages/Bio";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-[#08080A] flex flex-col items-center justify-center gap-6 p-8 text-center">
          <LogoMark size={64} />
          <h1 className="font-display text-2xl font-bold uppercase text-white">The world hit a snag</h1>
          <p className="text-zinc-300">Something interrupted CHILLWOAH. A refresh usually fixes it.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function Splash() {
  return (
    <div className="fixed inset-0 z-[100] bg-[#08080A] flex flex-col items-center justify-center gap-6">
      <div className="animate-lux-pulse">
        <LogoMark size={72} />
      </div>
      <p className="font-mono text-xs tracking-[0.35em] text-[#F26522] uppercase">Entering the world</p>
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

/** Lenis smooth momentum scrolling — skipped entirely for reduced-motion users. */
function SmoothScroll() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let lenis;
    let raf;
    let cancelled = false;
    (async () => {
      const Lenis = (await import("lenis")).default;
      if (cancelled) return;
      lenis = new Lenis({ duration: 1.15, smoothWheel: true });
      const loop = (t) => {
        lenis.raf(t);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    })();
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      if (lenis) lenis.destroy();
    };
  }, []);
  return null;
}

function AppInner() {
  const { loading } = useContent();

  // Website-exclusive player content — a single unreleased track, never linked
  // to any streaming platform.
  const tracks = [
    {
      id: "the-dubau",
      title: "THE DUBAU",
      artist: "Okaytwitchy",
      cover: "/audio/the-dubau-cover.webp",
      src: "/audio/the-dubau.mp3",
    },
  ];

  if (loading) return <Splash />;

  return (
    <AudioProvider tracks={tracks}>
      <BrowserRouter>
        <ScrollToTop />
        <SmoothScroll />
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[110] focus:bg-[#F26522] focus:text-black focus:px-6 focus:py-3 focus:font-display focus:font-bold"
        >
          SKIP TO CONTENT
        </a>
        <Nav />
        <main id="main" className="pb-32 sm:pb-24">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/music" element={<Music />} />
            <Route path="/videos" element={<Videos />} />
            <Route path="/passport" element={<Passport />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/bio" element={<Bio />} />
            <Route path="*" element={<Home />} />
          </Routes>
          <Footer />
        </main>
        <PlayerBar />
        <CartDrawer />
      </BrowserRouter>
      <Toaster position="top-center" theme="dark" />
    </AudioProvider>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ContentProvider>
        <CartProvider>
          <AppInner />
        </CartProvider>
      </ContentProvider>
    </ErrorBoundary>
  );
}
